import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, Loader2, Package, Truck, CreditCard, MapPin, Banknote } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [["all", "All"], ["paid", "Paid"], ["pending", "Pending"], ["refunded", "Refunded"], ["expired", "Expired"]];

const payBadge = (s) => {
  const map = {
    paid: "border-green-600/40 text-green-700 bg-green-50",
    pending: "border-amber-500/40 text-amber-700 bg-amber-50",
    refunded: "border-rose-500/40 text-rose-700 bg-rose-50",
    expired: "border-border text-muted-foreground",
  };
  return map[s] || "border-border text-muted-foreground";
};
const fulBadge = (s) => ({
  delivered: "border-green-600/40 text-green-700 bg-green-50",
  shipped: "border-blue-500/40 text-blue-700 bg-blue-50",
}[s] || "border-border text-muted-foreground");

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function AdminOrders() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ orders: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState("");
  const [ful, setFul] = useState({ fulfillment_status: "unfulfilled", tracking_carrier: "", tracking_number: "" });

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/orders${f && f !== "all" ? `?status=${f}` : ""}`);
      setData(data);
    } catch (e) { toast.error("Could not load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(filter); }, [filter]);

  if (!user || user.role !== "admin")
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Admin only.</div>;

  const openOrder = async (oid) => {
    try {
      const { data } = await api.get(`/admin/orders/${oid}`);
      setSel(data);
      setFul({
        fulfillment_status: data.fulfillment_status || "unfulfilled",
        tracking_carrier: data.tracking_carrier || "",
        tracking_number: data.tracking_number || "",
      });
    } catch (e) { toast.error("Could not open order"); }
  };

  const refreshSel = (o) => { setSel(o); load(filter); };

  const syncPayment = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post(`/admin/orders/${sel.id}/sync`);
      toast.success(`Stripe says: payment ${data.stripe_payment_status || "unknown"}${data.stripe_session_status ? `, session ${data.stripe_session_status}` : ""}`);
      if (data.order) refreshSel(data.order);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const runPayout = async () => {
    setBusy("payout");
    try { await api.post(`/admin/orders/${sel.id}/payout`); toast.success("Payout processed"); openOrder(sel.id); load(filter); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const refund = async () => {
    if (!window.confirm("Refund this order to the customer? Seller transfers will be reversed.")) return;
    setBusy("refund");
    try { await api.post(`/admin/orders/${sel.id}/refund`); toast.success("Order refunded"); openOrder(sel.id); load(filter); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const saveFulfillment = async () => {
    setBusy("ful");
    try { const { data } = await api.put(`/orders/${sel.id}/fulfillment`, ful); toast.success("Fulfillment updated"); refreshSel({ ...sel, ...data }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const orders = data.orders;
  const addr = sel?.shipping_address;

  return (
    <div data-testid="admin-orders" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="mb-8">
        <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Admin · Orders</div>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Orders</h1>
        <p className="text-muted-foreground mt-2 text-sm">{data.total} order{data.total === 1 ? "" : "s"} total. Click any order for full details.</p>
      </div>

      <div className="flex gap-6 border-b border-border mb-6 flex-wrap">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`orders-filter-${k}`}
            className={`pb-3 text-sm tracking-wide -mb-px border-b-2 transition-colors ${filter === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}{k !== "all" && data.counts[k] ? ` (${data.counts[k]})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-border rounded-sm">No orders in this view.</div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-wider uppercase text-muted-foreground border-b border-border">
                <th className="px-4 py-3">Order</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Fulfillment</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id.slice(0, 8)}`} onClick={() => openOrder(o.id)}
                    className="border-b border-border/50 last:border-0 hover:bg-secondary/40 cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3">{o.buyer_name || o.buyer_email}</td>
                  <td className="px-4 py-3">{money(o.total)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${payBadge(o.status)}`}>{o.status}</span></td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${fulBadge(o.fulfillment_status)}`}>{o.fulfillment_status || "unfulfilled"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DETAIL */}
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="order-detail" className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl flex items-center gap-3">
                  Order #{sel.id.slice(0, 8)}
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${payBadge(sel.status)}`}>{sel.status}</span>
                </DialogTitle>
              </DialogHeader>

              {sel.status === "pending" && (
                <div className="text-xs bg-amber-50 border border-amber-500/40 text-amber-800 rounded-sm px-3 py-2">
                  This order is <strong>pending</strong> — the shopper started checkout but Stripe has not confirmed payment (usually an abandoned/incomplete checkout). Use "Check payment status" to confirm with Stripe.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2 text-sm">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Order</div>
                  <div className="text-muted-foreground">Placed: {fmtDate(sel.created_at)}</div>
                  {sel.paid_at && <div className="text-muted-foreground">Paid: {fmtDate(sel.paid_at)}</div>}
                  <div className="text-muted-foreground">Ship-to region: {sel.ship_to_country || "—"}</div>
                </div>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Customer</div>
                  <div>{sel.customer_details?.name || sel.buyer_name || "—"}</div>
                  <div className="text-muted-foreground">{sel.buyer_email}</div>
                  {sel.buyer_phone && <div className="text-muted-foreground">{sel.buyer_phone}</div>}
                </div>

                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Shipping address</div>
                  {addr && (addr.line1 || addr.city) ? (
                    <div className="text-muted-foreground leading-relaxed">
                      {addr.name && <div>{addr.name}</div>}
                      {addr.line1 && <div>{addr.line1}</div>}
                      {addr.line2 && <div>{addr.line2}</div>}
                      <div>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}</div>
                      {addr.country && <div>{addr.country}</div>}
                    </div>
                  ) : <div className="text-muted-foreground">Not captured (older order or unpaid)</div>}
                </div>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><CreditCard className="w-3 h-3" /> Payment</div>
                  <div className="text-muted-foreground">Status: {sel.status}</div>
                  <div className="text-muted-foreground break-all">Payment intent: {sel.payment_intent_id || "—"}</div>
                  <div className="text-muted-foreground break-all">Charge: {sel.charge_id || "—"}</div>
                  <div className="text-muted-foreground break-all">Session: {sel.stripe_session_id || "—"}</div>
                </div>
              </div>

              {/* Items */}
              <div className="mt-4">
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Package className="w-3 h-3" /> Products</div>
                <div className="border border-border rounded-sm divide-y divide-border">
                  {(sel.items || []).map((it, i) => (
                    <div key={i} className="px-4 py-3 flex justify-between text-sm">
                      <div>
                        <div>{it.quantity}× {it.title}{it.variant_label ? ` (${it.variant_label})` : ""}</div>
                        <div className="text-xs text-muted-foreground">Seller: {it.seller_name || it.seller_id?.slice(0, 8)}</div>
                      </div>
                      <div>{money(it.price * it.quantity)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 space-y-1 text-sm text-right">
                  <div className="text-muted-foreground">Subtotal: {money(sel.subtotal)}</div>
                  <div className="text-muted-foreground">Shipping: {money(sel.shipping_total)}</div>
                  <div className="text-muted-foreground">Platform commission (8%): {money(sel.platform_commission)}</div>
                  <div className="font-serif text-lg">Total: {money(sel.total)}</div>
                </div>
              </div>

              {/* Payouts */}
              <div className="mt-4">
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Banknote className="w-3 h-3" /> Seller payouts</div>
                <div className="border border-border rounded-sm divide-y divide-border">
                  {(sel.seller_payouts || []).map((p, i) => (
                    <div key={i} className="px-4 py-3 flex justify-between text-sm">
                      <div>
                        <div>{p.seller_name}</div>
                        <div className="text-xs text-muted-foreground">Payout {money(p.payout_amount)} · commission {money(p.commission)}</div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border self-center ${p.transfer_status === "paid" ? "border-green-600/40 text-green-700 bg-green-50" : p.transfer_status === "reversed" ? "border-rose-500/40 text-rose-700 bg-rose-50" : "border-border text-muted-foreground"}`}>
                        {p.transfer_status || "pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fulfillment / tracking */}
              <div className="mt-4 border border-border rounded-sm p-4">
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3 flex items-center gap-1.5"><Truck className="w-3 h-3" /> Fulfillment & tracking</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground">Status</label>
                    <select data-testid="fulfillment-status" value={ful.fulfillment_status}
                      onChange={(e) => setFul({ ...ful, fulfillment_status: e.target.value })}
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-2 mt-1">
                      <option value="unfulfilled">Unfulfilled</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Carrier</label>
                    <input data-testid="tracking-carrier" value={ful.tracking_carrier} placeholder="e.g. Canada Post"
                      onChange={(e) => setFul({ ...ful, tracking_carrier: e.target.value })}
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-2 mt-1" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Tracking #</label>
                    <input data-testid="tracking-number" value={ful.tracking_number} placeholder="Tracking number"
                      onChange={(e) => setFul({ ...ful, tracking_number: e.target.value })}
                      className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-2 mt-1" />
                  </div>
                </div>
                <button onClick={saveFulfillment} data-testid="save-fulfillment-btn" disabled={busy === "ful"}
                  className="btn-primary mt-4 px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2">
                  {busy === "ful" && <Loader2 className="w-4 h-4 animate-spin" />} Save fulfillment
                </button>
              </div>

              {/* Actions */}
              <div className="mt-5 flex flex-wrap gap-3 border-t border-border pt-5">
                <button onClick={syncPayment} data-testid="check-payment-btn" disabled={busy === "sync"}
                  className="border border-border px-5 py-2.5 text-sm rounded-sm hover:bg-secondary inline-flex items-center gap-2">
                  {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check payment status
                </button>
                {sel.status === "paid" && (
                  <button onClick={runPayout} data-testid="run-payout-btn" disabled={busy === "payout"}
                    className="border border-border px-5 py-2.5 text-sm rounded-sm hover:bg-secondary inline-flex items-center gap-2">
                    {busy === "payout" && <Loader2 className="w-4 h-4 animate-spin" />} Run payout
                  </button>
                )}
                {sel.status === "paid" && (
                  <button onClick={refund} data-testid="refund-btn" disabled={busy === "refund"}
                    className="border border-rose-400 text-rose-600 px-5 py-2.5 text-sm rounded-sm hover:bg-rose-50 inline-flex items-center gap-2">
                    {busy === "refund" && <Loader2 className="w-4 h-4 animate-spin" />} Refund order
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
